import request from '@/utils/request'

const langCache = {}

const LANG_ALIASES = {
    CN: 'CN',
    'ZH-CN': 'CN',
    'ZH_CN': 'CN',
    中文: 'CN',
    简体中文: 'CN',
    EN: 'EN',
    'EN-US': 'EN',
    'EN_US': 'EN',
    ENGLISH: 'EN',
    英文: 'EN',
    YN: 'YN',
    'ZH-TW': 'YN',
    'ZH_TW': 'YN',
    'ZH-HANT': 'YN',
    'ZH_HANT': 'YN',
    繁体: 'YN',
    繁體: 'YN',
    繁体中文: 'YN',
    繁體中文: 'YN'
}

const FIELD_KEY_NAMES = ['key', 'KEY', 'FLDNO', 'fldno']
const CN_TITLE_NAMES = ['FLDNM', 'fldnm', 'title', 'TITLE', 'CNAME', 'cname']
const EN_TITLE_NAMES = ['FLDEN', 'fldEN', 'ename', 'ENAME', 'enname', 'ENNAME']
const YN_TITLE_NAMES = ['FLDYN', 'fldyn', 'yname', 'YNAME', 'ynname', 'YNNAME', 'twname', 'TWNAME']
const ROUTE_CN_TITLE_NAMES = ['name', 'NAME', 'CNAME', 'cname', 'FLDNM', 'fldnm', 'title', 'TITLE', 'label', 'LABEL', 'CN']
const ROUTE_EN_TITLE_NAMES = ['ename', 'ENAME', 'FLDEN', 'fldEN', 'enname', 'ENNAME', 'EN']
const ROUTE_YN_TITLE_NAMES = ['yname', 'YNAME', 'FLDYN', 'fldyn', 'ynname', 'YNNAME', 'twname', 'TWNAME', 'YN']

function getCacheKey(lstseq, idseq) {
    return `${lstseq}_${idseq}`
}

function normalizeLang(lang) {
    const value = String(lang == null ? '' : lang).trim()
    if (!value) return 'CN'
    const upperValue = value.toUpperCase()
    return LANG_ALIASES[value] || LANG_ALIASES[upperValue] || 'CN'
}

function isLangCode(lang) {
    const value = String(lang == null ? '' : lang).trim()
    return Boolean(LANG_ALIASES[value] || LANG_ALIASES[value.toUpperCase()])
}

function hasText(value) {
    return value !== null && value !== undefined && (typeof value !== 'string' || value.trim() !== '')
}

function textValue(value) {
    if (!hasText(value)) return ''
    return typeof value === 'string' ? value.trim() : String(value)
}

function readValue(item, names) {
    if (!item || typeof item !== 'object') return ''

    for (let i = 0; i < names.length; i += 1) {
        const name = names[i]
        if (Object.prototype.hasOwnProperty.call(item, name)) {
            const value = textValue(item[name])
            if (value) return value
        }
    }

    // Backend revisions have used different casing for the same metadata field.
    const lowerKeys = Object.keys(item).reduce((result, key) => {
        result[key.toLowerCase()] = key
        return result
    }, {})
    for (let i = 0; i < names.length; i += 1) {
        const actualName = lowerKeys[String(names[i]).toLowerCase()]
        if (actualName) {
            const value = textValue(item[actualName])
            if (value) return value
        }
    }
    return ''
}

function normalizeKey(key) {
    const value = textValue(key)
    return value ? value.toUpperCase() : ''
}

function getFieldKey(item) {
    return readValue(item, FIELD_KEY_NAMES)
}

function getChineseTitle(item) {
    return readValue(item, CN_TITLE_NAMES)
}

function getColTitle(item, lang = 'CN') {
    if (!item) return ''
    const chineseTitle = getChineseTitle(item)
    const normalizedLang = normalizeLang(lang)
    let translatedTitle = chineseTitle
    if (normalizedLang === 'EN') translatedTitle = readValue(item, EN_TITLE_NAMES)
    if (normalizedLang === 'YN') translatedTitle = readValue(item, YN_TITLE_NAMES)
    return translatedTitle || chineseTitle
}

function getRouteTitleValue(item, lang) {
    if (!item) return ''
    const normalizedLang = normalizeLang(lang)
    const source = item.meta && typeof item.meta === 'object'
        ? Object.assign({}, item, item.meta)
        : item
    const chineseTitle = readValue(source, ROUTE_CN_TITLE_NAMES)
    let translatedTitle = chineseTitle
    if (normalizedLang === 'EN') translatedTitle = readValue(source, ROUTE_EN_TITLE_NAMES)
    if (normalizedLang === 'YN') translatedTitle = readValue(source, ROUTE_YN_TITLE_NAMES)
    return translatedTitle || chineseTitle
}

function getMenuTitle(item, lang = 'CN', fallback = '') {
    return getRouteTitleValue(item, lang) || textValue(fallback)
}

function getRoutePath(route) {
    const path = typeof route === 'string' ? route : route && route.path
    if (!path) return ''
    const value = String(path).split(/[?#]/)[0]
    if (!value) return ''
    return value.charAt(0) === '/' ? value : `/${value}`
}

function getMappedRouteTitle(path, titleMap) {
    if (!path || !titleMap || typeof titleMap !== 'object') return null
    if (titleMap[path]) return titleMap[path]
    const normalizedPath = getRoutePath(path)
    if (titleMap[normalizedPath]) return titleMap[normalizedPath]
    const mapKey = Object.keys(titleMap).find(key => getRoutePath(key) === normalizedPath)
    return mapKey ? titleMap[mapKey] : null
}

/**
 * Resolve a route/menu title from one source for the sidebar, tabs and breadcrumb.
 * The fourth argument is the store's menuTitleMap; route meta remains the final
 * Chinese fallback when a menu response has not arrived yet.
 */
function getRouteTitle(route, lang = 'CN', fallback = '', titleMap = {}) {
    // Keep the old (route, fallback, lang, map) call shape usable for callers
    // outside the shell while the shell uses the explicit language-first shape.
    if (!isLangCode(lang)) {
        const legacyFallback = lang
        lang = fallback
        fallback = legacyFallback
        titleMap = arguments[3] || {}
    }

    const path = getRoutePath(route)
    const mappedTitle = getMappedRouteTitle(path, titleMap)
    const routeTitle = getRouteTitleValue(mappedTitle, lang)
    if (routeTitle) return routeTitle

    const directTitle = getRouteTitleValue(route, lang)
    if (directTitle) return directTitle
    return textValue(fallback)
}

async function loadLangPack(lstseq, idseq) {
    const cacheKey = getCacheKey(lstseq, idseq)

    if (langCache[cacheKey]) {
        return langCache[cacheKey]
    }

    const res = await request.post('xuper_api/getBaseData', {
        p_key: 'lstseq',
        p_value: lstseq,
        p_key2: 'idseq',
        p_value2: idseq,
        p_table: 'V_SYS_form_fld'
    })

    if (!res || String(res.code) !== '200') {
        throw new Error(res && res.result ? res.result : '语言包加载失败')
    }

    const map = {}
    const translationRows = res.result || []
    translationRows.forEach(item => {
        const rawKey = getFieldKey(item)
        const key = normalizeKey(rawKey)
        if (!key) return
        const entry = {
            CN: getChineseTitle(item),
            EN: readValue(item, EN_TITLE_NAMES),
            YN: readValue(item, YN_TITLE_NAMES)
        }
        map[key] = entry
        // Preserve the raw backend key while keeping case-insensitive lookup.
        if (rawKey && rawKey !== key) map[rawKey] = entry
    })

    langCache[cacheKey] = map
    return map
}

function getLoadedLangPack(lstseq, idseq) {
    return langCache[getCacheKey(lstseq, idseq)] || {}
}

function findLangEntry(langPack, key) {
    if (!langPack || typeof langPack !== 'object') return null
    const normalizedKey = normalizeKey(key)
    if (!normalizedKey) return null
    if (langPack[normalizedKey]) return langPack[normalizedKey]

    const directKey = textValue(key)
    if (directKey && langPack[directKey]) return langPack[directKey]
    const actualKey = Object.keys(langPack).find(itemKey => normalizeKey(itemKey) === normalizedKey)
    return actualKey ? langPack[actualKey] : null
}

function findLangEntryByChinese(langPack, fallback) {
    const target = textValue(fallback)
    if (!target || !langPack || typeof langPack !== 'object') return null
    const entries = Object.keys(langPack)
    for (let i = 0; i < entries.length; i += 1) {
        const entry = langPack[entries[i]]
        if (entry && textValue(entry.CN || entry.FLDNM || entry.title) === target) return entry
    }
    return null
}

function getLangText(langPack, lang, key, def = '') {
    const entry = findLangEntry(langPack, key) || findLangEntryByChinese(langPack, def)
    if (!entry) return def

    const normalizedLang = normalizeLang(lang)
    const selected = normalizedLang === 'CN'
        ? readValue(entry, ['CN'])
        : normalizedLang === 'EN'
            ? readValue(entry, ['EN', 'FLDEN'])
            : readValue(entry, ['YN', 'FLDYN'])
    const chinese = readValue(entry, ['CN', 'FLDNM', 'title', 'CNAME'])
    return selected || chinese || def
}

function createLangGetter(vm, lstseq, idseq) {
    return (key, def = '') => {
        const lang = vm && vm.$store && vm.$store.state ? vm.$store.state.lang : 'CN'
        const langPack = getLoadedLangPack(lstseq, idseq)
        return getLangText(langPack, lang, key, def)
    }
}

export default {
    normalizeLang,
    getFieldKey,
    getChineseTitle,
    getMenuTitle,
    getRouteTitle,
    loadLangPack,
    createLangGetter,
    getColTitle,
    getLangText,
    getLoadedLangPack
}
